/**
 * 배관 부속의 등가길이 환산.
 *
 * 실무에서 널리 쓰는 방식은 부속을 **등가길이 Le** 로 바꿔 직관 길이에 더한 뒤,
 * 레이놀즈수로 구한 실제 마찰계수로 한 번에 압력손실을 계산하는 것이다.
 *
 *   L_total = L_직관 + Σ( (L/D)ᵢ × Dᵢ × 개수ᵢ )
 *   ΔP = f × (L_total / D) × ρv²/2
 *
 * ── 두 방식의 차이를 알고 써야 한다 ────────────────────────
 * ① 등가길이(L/D) 방식 — 위 식. 부속 손실도 실제 f 에 비례한다고 본다.
 * ② 손실계수(K) 방식  — K = fT × (L/D) 로 미리 굳혀 두고 ΔP = ΣK × ρv²/2.
 *    여기서 fT 는 **완전난류 마찰계수**이며 레이놀즈수와 무관하다.
 *
 * Crane TP-410 의 원래 취지는 ②다. 부속의 손실은 대체로 완전난류라 Re 에
 * 둔감하기 때문이다. ①은 계산이 간단해 널리 쓰이지만, 층류나 저Re 영역에서는
 * 부속 손실을 과대평가한다. PRV 입구배관은 대개 고Re 난류라 두 방식의 차이가
 * 작지만, **어느 방식을 썼는지 계산서에 남겨야 한다.**
 *
 * ── L/D 값의 출처 ────────────────────────────────────────
 * **KOSHA GUIDE D-63-2018 「안전밸브 배출배관의 설계에 관한 기술지침」 <표1>**
 * 을 기준으로 삼는다. API 표준에는 이 표가 없고, 국내 안전밸브 배출배관
 * 설계는 이 지침을 따르므로 여기에 맞춘다.
 *
 * 표에 없는 항목(볼 밸브, 축소관, 입구·출구 손실)은 업계 통용값을 쓰고
 * `note` 에 그 사실을 적는다.
 *
 * K 값은 KOSHA 표에 없으므로 K = fT × (L/D), fT = 0.015 로 환산했다.
 *
 * ⚠ 사내 기준이나 Crane TP-410 이 있으면 그쪽을 우선한다.
 */

export type FittingKind =
  | "entrance-sharp"
  | "entrance-rounded"
  | "entrance-reentrant"
  | "exit"
  | "elbow90-long"
  | "elbow90-standard"
  | "elbow90-short"
  | "elbow45"
  | "tee-through"
  | "tee-branch"
  | "gate-valve"
  | "butterfly-valve"
  | "angle-valve"
  | "ball-valve"
  | "globe-valve"
  | "check-valve-swing"
  | "check-valve-clearway"
  | "mitre-bend-90"
  | "return-bend"
  | "tee-unequal"
  | "reducer-gradual";

interface FittingSpec {
  readonly label: string;
  /**
   * 등가길이비 L/D. 입구·출구처럼 길이 개념이 없는 것은 null 이고,
   * 이때는 손실계수 K 를 그대로 쓴다.
   */
  readonly lOverD: number | null;
  /** 손실계수 K (완전난류 기준) */
  readonly k: number;
  readonly note?: string;
}

/**
 * 부속별 등가길이비와 손실계수.
 *
 * 입구·출구 손실은 배관 길이로 환산하는 개념이 아니라 속도수두에 직접
 * 비례하므로 K 로만 다룬다. API 520 Part II 7.3.7.2 a) 가 **용기에서
 * 배관으로 들어가는 입구 손실을 반드시 포함하라**고 명시한다.
 */
export const FITTINGS: Record<FittingKind, FittingSpec> = {
  "entrance-sharp": {
    label: "입구 (각진 형상)",
    lOverD: null,
    k: 0.5,
    note: "용기 → 배관. 7.3.7.2 a) 에 따라 반드시 포함",
  },
  "entrance-rounded": {
    label: "입구 (둥근 형상)",
    lOverD: null,
    k: 0.05,
    note: "용기 → 배관. 7.3.7.2 a) 에 따라 반드시 포함",
  },
  "entrance-reentrant": {
    label: "입구 (돌출형)",
    lOverD: null,
    k: 0.78,
    note: "용기 → 배관. 7.3.7.2 a) 에 따라 반드시 포함",
  },
  exit: { label: "출구 (배관 → 용기·대기)", lOverD: null, k: 1.0 },
  "elbow90-long": {
    label: "90° 엘보 (장반경 LR)",
    lOverD: 20,
    k: 0.3,
    note: "KOSHA D-63 <표1> 8-1)",
  },
  "elbow90-standard": {
    label: "90° 엘보 (단반경 SR)",
    lOverD: 30,
    k: 0.45,
    note: "KOSHA D-63 <표1> 8-2)",
  },
  "elbow90-short": {
    label: "스트리트 엘보 90°",
    lOverD: 50,
    k: 0.75,
    note: "KOSHA D-63 <표1> 10-1)",
  },
  "mitre-bend-90": {
    label: "마이터 벤드 90° (단일)",
    lOverD: 58,
    k: 0.87,
    note: "KOSHA D-63 <표1> 11-2)",
  },
  "return-bend": {
    label: "리턴 벤드 (180°)",
    lOverD: 50,
    k: 0.75,
    note: "KOSHA D-63 <표1> 12",
  },
  elbow45: {
    label: "45° 엘보 (단반경 SR)",
    lOverD: 16,
    k: 0.24,
    note: "KOSHA D-63 <표1> 8-3)",
  },
  "tee-through": {
    label: "티 (직진)",
    lOverD: 20,
    k: 0.3,
    note: "KOSHA D-63 <표1> 9-1)",
  },
  "tee-branch": {
    label: "티 (분기)",
    lOverD: 60,
    k: 0.9,
    note: "KOSHA D-63 <표1> 9-2)",
  },
  "tee-unequal": {
    label: "티 (부등경)",
    lOverD: 100,
    k: 1.5,
    note: "KOSHA D-63 <표1> 9-3)",
  },
  "gate-valve": {
    label: "게이트 밸브 (웨지·전개)",
    lOverD: 13,
    k: 0.2,
    note: "KOSHA D-63 <표1> 3-1). 반개 260, 1/4개 900 으로 급증한다",
  },
  "butterfly-valve": {
    label: "버터플라이 밸브 (전개)",
    lOverD: 40,
    k: 0.6,
    note: "KOSHA D-63 <표1> 6",
  },
  "angle-valve": {
    label: "앵글 밸브 (플러그 시트·전개)",
    lOverD: 145,
    k: 2.18,
    note: "KOSHA D-63 <표1> 2-1)",
  },
  "ball-valve": {
    label: "볼 밸브 (풀보어·전개)",
    lOverD: 3,
    k: 0.05,
    note: "⚠ KOSHA <표1> 에 없는 항목. 업계 통용값이다",
  },
  "globe-valve": {
    label: "글로브 밸브 (플러그 시트·전개)",
    lOverD: 340,
    k: 5.1,
    note: "KOSHA D-63 <표1> 1-1). 손실이 매우 크다 — PRV 입구배관에는 쓰지 않는 것이 원칙",
  },
  "check-valve-swing": {
    label: "체크 밸브 (스윙)",
    lOverD: 135,
    k: 2.03,
    note: "KOSHA D-63 <표1> 4-1)",
  },
  "check-valve-clearway": {
    label: "체크 밸브 (클리어웨이 스윙)",
    lOverD: 50,
    k: 0.75,
    note: "KOSHA D-63 <표1> 4-2). 스윙형보다 손실이 훨씬 작다",
  },
  "reducer-gradual": {
    label: "축소관 (점진)",
    lOverD: 12,
    k: 0.18,
    note: "⚠ KOSHA <표1> 에 없는 항목. 형상·축소비에 따라 크게 달라지므로 제작 도면으로 확인할 것",
  },
};

export const FITTING_KINDS = Object.keys(FITTINGS) as FittingKind[];

/** 배관에 달린 부속 하나 (종류 + 개수). */
export interface FittingItem {
  readonly kind: FittingKind;
  readonly count: number;
}

export interface FittingTotals {
  /** 등가길이 합 (m) — L/D 가 정의된 부속만 */
  readonly equivalentLengthM: number;
  /** 손실계수 합 ΣK — 입구·출구처럼 L/D 가 없는 부속 */
  readonly headOnlyK: number;
  /** 전부 K 로 환산했을 때의 합 (참고·비교용) */
  readonly totalKEquivalent: number;
  /** 부속별 내역 */
  readonly breakdown: ReadonlyArray<{
    readonly kind: FittingKind;
    readonly label: string;
    readonly count: number;
    readonly equivalentLengthM: number;
    readonly k: number;
  }>;
}

/**
 * 부속 목록을 등가길이와 손실계수로 환산한다.
 *
 * @param fittings 부속 목록
 * @param innerDiameterM 그 배관의 내경 (m) — 등가길이는 관경에 비례한다
 */
export function fittingTotals(
  fittings: readonly FittingItem[],
  innerDiameterM: number,
): FittingTotals {
  let equivalentLengthM = 0;
  let headOnlyK = 0;
  let totalKEquivalent = 0;
  const breakdown: Array<{
    kind: FittingKind;
    label: string;
    count: number;
    equivalentLengthM: number;
    k: number;
  }> = [];

  for (const item of fittings) {
    const spec = FITTINGS[item.kind];
    const n = Math.max(0, item.count);
    const le = spec.lOverD === null ? 0 : spec.lOverD * innerDiameterM * n;
    const k = spec.k * n;

    equivalentLengthM += le;
    if (spec.lOverD === null) headOnlyK += k;
    totalKEquivalent += k;

    breakdown.push({
      kind: item.kind,
      label: spec.label,
      count: n,
      equivalentLengthM: le,
      k,
    });
  }

  return { equivalentLengthM, headOnlyK, totalKEquivalent, breakdown };
}
